import React from 'react';

interface PrizmaLogoProps {
  size?: number;
  showTagline?: boolean;
  className?: string;
}

export default function PrizmaLogo({ size = 36, showTagline = false, className = '' }: PrizmaLogoProps) {
  // Triangle: apex at (72, 60), base corners at (8, 10) and (8, 110)
  // Triangle "arm" length from apex to base corner = sqrt((72-8)^2 + (60-10)^2) = sqrt(64^2 + 50^2) ≈ 81
  // Rays fan to the RIGHT from apex, symmetric around horizontal (0°)
  // 6 rays evenly spaced: -50, -30, -10, +10, +30, +50 degrees
  const apex = { x: 72, y: 60 };
  const rayLength = 81; // matches triangle arm length exactly
  const angles = [-50, -30, -10, 10, 30, 50];
  const colors = ['#9B59B6', '#3498DB', '#1ABC9C', '#F39C12', '#E74C3C', '#E91E8C'];

  const rays = angles.map((deg, i) => {
    const rad = (deg * Math.PI) / 180;
    const x2 = apex.x + rayLength * Math.cos(rad);
    const y2 = apex.y + rayLength * Math.sin(rad);
    return { x2, y2, color: colors[i] };
  });

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 160 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Prizma logo — triangular prism with rainbow rays"
      >
        {/* No background — fully transparent */}

        {/* Triangle: black in light mode, white in dark mode */}
        <polygon points="8,10 8,110 72,60" className="fill-gray-900 dark:fill-[#F0EDE8]" />

        {/* 6 colorful rays fanning symmetrically from apex to the right */}
        {rays.map((ray, i) => (
          <line
            key={i}
            x1={apex.x}
            y1={apex.y}
            x2={ray.x2}
            y2={ray.y2}
            stroke={ray.color}
            strokeWidth="7"
            strokeLinecap="butt"
          />
        ))}
      </svg>

      <div className="flex flex-col justify-center">
        <span
          className="font-extrabold text-foreground tracking-tight leading-none"
          style={{ fontSize: size * 0.55 }}
        >
          prizma
        </span>
        {showTagline && (
          <span
            className="text-muted-foreground tracking-widest uppercase leading-none"
            style={{ fontSize: size * 0.22, letterSpacing: '0.18em', marginTop: size * 0.1 }}
          >
            everything, in every color
          </span>
        )}
      </div>
    </div>
  );
}